import dayjs from 'dayjs';

/**
 * Fallback config used when no PharmacyNoteConfig document exists for a pharmacy.
 * Mirrors `DEFAULT_PHARMACY_NOTE_CONFIG` on the backend.
 */
export const DEFAULT_PHARMACY_NOTE_CONFIG = {
    maxCharacters: 210,
    noteTemplate: '{{pharmacyNotes}} - Brand: {{clientName}}, LTC {{date}}',
};

/**
 * Client-side preview of the final composed pharmacy note.
 *
 * Mirrors the backend `interpolateTemplate` in get-pharmacy-specific-notes.js.
 *
 * @param {string} template        - The noteTemplate string from PharmacyNoteConfig
 * @param {object} opts
 * @param {string} opts.pharmacyNotes  - The provider-entered notes
 * @param {boolean} opts.daw           - Dispense As Written flag
 * @param {string|number} [opts.patientId] - Patient MongoDB ID
 * @param {string|null} [opts.clientName]  - Client/brand name; falls back to '[client]' if unknown
 * @returns {string}
 */
export const previewComposedNote = (template, { pharmacyNotes = '', daw = false, patientId = '', clientName = null } = {}) => {
    const date   = dayjs().tz('UTC').format('MM/DD/YYYY');
    const dateET = dayjs().tz('America/New_York').format('MM/DD/YYYY');
    return template
        .replace(/\{\{pharmacyNotes\}\}/g, pharmacyNotes)
        .replace(/\{\{clientName\}\}/g, clientName ?? '[client]')
        .replace(/\{\{dateET\}\}/g, dateET)
        .replace(/\{\{date\}\}/g, date)
        .replace(/\{\{patientId\}\}/g, patientId != null ? String(patientId) : '')
        .replace(/\{\{daw\}\}/g, daw ? 'DAW, ' : '');
};

/**
 * Returns true if a noteTemplate contains anything beyond the bare
 * `{{pharmacyNotes}}` placeholder, i.e. the pharmacy appends its own suffix.
 *
 * @param {string} template
 * @returns {boolean}
 */
export const templateHasSuffix = (template) =>
    typeof template === 'string' && template.trim() !== '{{pharmacyNotes}}';

/**
 * Canonical catalog of template variables recognized across pharmacy-note
 * related UIs (provider Edit Treatment modal, admin Prescription Template
 * form, etc.). Individual UIs may render a subset depending on context.
 */
export const TEMPLATE_VARIABLE_DESCRIPTIONS = [
    { variable: '{{pharmacyNotes}}', description: 'Your notes' },
    { variable: '{{clientName}}',    description: 'Client / brand name' },
    { variable: '{{date}}',          description: "Today's date (MM/DD/YYYY, UTC)" },
    { variable: '{{dateET}}',        description: "Today's date (MM/DD/YYYY, Eastern time)" },
    { variable: '{{patientId}}',     description: 'Patient ID' },
    { variable: '{{daw}}',           description: '"DAW, " when Dispense As Written is checked' },
    { variable: '{{CDS}}',           description: "Clinical Decision Support option selected by the provider from the telemedicine category's CDS list" },
];

/** Bare variable names (no braces) derived from {@link TEMPLATE_VARIABLE_DESCRIPTIONS}. */
export const SPECIAL_VARIABLE_NAMES = TEMPLATE_VARIABLE_DESCRIPTIONS.map(
    ({ variable }) => variable.replace(/[{}]/g, '')
);

export const CDS_VARIABLE_NAME = 'CDS';

/** Matches `{{foo}}` / `{{ foo }}` tokens and captures the inner name. */
export const PHARMACY_NOTES_VARIABLE_REGEX = /\{\{\s*([^}]+?)\s*\}\}/g;

/**
 * Case-insensitive check for the `{{CDS}}` template variable.
 *
 * @param {string} name  The bare variable name (no curly braces).
 * @returns {boolean}
 */
export const isCdsVariable = (name) =>
    typeof name === 'string' && name.toUpperCase() === CDS_VARIABLE_NAME;

/**
 * Find `{{...}}` tokens in a template whose normalized name matches one of
 * `specialNames` by case-insensitive comparison but differs in exact casing.
 * Used to surface "did you mean {{CDS}}?" style warnings in admin UIs.
 *
 * @param {string} template
 * @param {string[]} [specialNames]  Defaults to {@link SPECIAL_VARIABLE_NAMES}.
 * @returns {string[]}  Human-readable "{{typed}} → {{Canonical}}" strings.
 */
export const findMiscasedSpecialVariables = (template, specialNames = SPECIAL_VARIABLE_NAMES) => {
    if (typeof template !== 'string' || !template) return [];
    const miscased = new Set();
    const regex = new RegExp(PHARMACY_NOTES_VARIABLE_REGEX.source, 'g');
    let match;
    while ((match = regex.exec(template)) !== null) {
        const token = match[1].trim();
        const canonical = specialNames.find(
            name => name.toUpperCase() === token.toUpperCase() && name !== token
        );
        if (canonical) miscased.add(`{{${token}}} → {{${canonical}}}`);
    }
    return [...miscased];
};
